import DOMPurify from "dompurify";

import {
  EditorJsBlock,
  EditorJsOutputData,
  TINYMCE_RICH_TEXT_TYPE,
  TinymceHtmlDocumentV1,
} from "./types";

type ParsedRichText =
  | { kind: "empty"; document: null; raw: unknown }
  | { kind: "editorjs"; document: EditorJsOutputData; raw: unknown }
  | { kind: "tinymce"; document: TinymceHtmlDocumentV1; raw: unknown }
  | { kind: "html"; document: null; html: string; raw: unknown }
  | { kind: "plain"; document: null; text: string; raw: unknown }
  | { kind: "unknown"; document: null; value: unknown; raw: unknown };

const ALLOWED_EMBED_HOSTS = new Set([
  "player.vimeo.com",
  "www.youtube.com",
  "youtube.com",
  "www.youtube-nocookie.com",
  "youtube-nocookie.com",
]);
// The EditorJS document version Saleor's own examples carry; the value is
// informational to Saleor, but it must be a string.
const EDITOR_JS_VERSION = "2.29.0";
const BLOCK_LEVEL_HTML_TAG_PATTERN =
  /<\/?(address|article|aside|blockquote|div|dl|fieldset|figcaption|figure|footer|form|h[1-6]|header|hr|li|main|nav|ol|p|pre|section|table|tbody|td|tfoot|th|thead|tr|ul)\b[^>]*>/i;

const SANITIZE_CONFIG = {
  ADD_ATTR: [
    "alt",
    "colspan",
    "height",
    "loading",
    "rel",
    "rowspan",
    "scope",
    "src",
    "target",
    "title",
    "width",
  ],
  FORBID_ATTR: ["style"],
};

const SANITIZE_WITH_IFRAMES_CONFIG = {
  ADD_TAGS: ["iframe"],
  ADD_ATTR: [
    "allow",
    "allowfullscreen",
    "frameborder",
    "height",
    "loading",
    "referrerpolicy",
    "src",
    "title",
    "width",
  ],
};

export function isTinymceHtmlDocument(value: unknown): value is TinymceHtmlDocumentV1 {
  return (
    isRecord(value) &&
    value.type === TINYMCE_RICH_TEXT_TYPE &&
    value.version === 1 &&
    value.editor === "tinymce" &&
    typeof value.html === "string" &&
    typeof value.plainText === "string"
  );
}

export function isEditorJsDocument(value: unknown): value is EditorJsOutputData {
  return isRecord(value) && Array.isArray(value.blocks);
}

export function parseRichText(raw: unknown): ParsedRichText {
  if (raw === null || raw === undefined || raw === "") {
    return { kind: "empty", document: null, raw };
  }

  if (typeof raw === "string") {
    const trimmed = raw.trim();

    if (!trimmed) {
      return { kind: "empty", document: null, raw };
    }

    try {
      return parseRichText(JSON.parse(trimmed));
    } catch {
      if (trimmed.startsWith("<")) {
        return { kind: "html", document: null, html: raw, raw };
      }

      return { kind: "plain", document: null, text: raw, raw };
    }
  }

  if (isTinymceHtmlDocument(raw)) {
    return { kind: "tinymce", document: raw, raw };
  }

  if (isEditorJsDocument(raw)) {
    return { kind: "editorjs", document: raw, raw };
  }

  return { kind: "unknown", document: null, value: raw, raw };
}

export function richTextToHtml(raw: unknown): string {
  const parsed = parseRichText(raw);

  switch (parsed.kind) {
    case "empty":
      return "";
    case "tinymce":
      return sanitizeHtml(parsed.document.html, { allowSafeIframes: true });
    case "editorjs":
      return sanitizeHtml(editorJsToHtml(parsed.document), { allowSafeIframes: true });
    case "html":
      return sanitizeHtml(parsed.html, { allowSafeIframes: true });
    case "plain":
      return `<p>${escapeHtml(parsed.text)}</p>`;
    case "unknown":
      return `<p>${escapeHtml(JSON.stringify(parsed.value))}</p>`;
  }
}

export function richTextToPlainText(raw: unknown): string {
  const parsed = parseRichText(raw);

  switch (parsed.kind) {
    case "empty":
      return "";
    case "tinymce":
      return parsed.document.plainText || htmlToPlainText(parsed.document.html);
    case "plain":
      return parsed.text;
    case "unknown":
      return JSON.stringify(parsed.value);
    case "editorjs":
    case "html":
      return htmlToPlainText(richTextToHtml(raw));
  }
}

/**
 * What the editor saves: a standard EditorJS document (WSM6-3450).
 *
 * Saleor validates every rich-text field against its EditorJS model and has no
 * notion of the `wsm.richText.html` wrapper this used to emit, so no product or
 * category description could be saved. Saleor's paragraph block cleans its
 * `text` with nh3 at nh3's default tag set, which keeps headings, lists,
 * tables, images and links, so the TinyMCE HTML travels as paragraph blocks;
 * both this app and the storefront already render a paragraph that carries its
 * own block tags (that is how migrated 5.0 content is stored). The one tag
 * Saleor's cleaner drops is `iframe`, so each safe embed is hoisted out of its
 * paragraph into an EditorJS `embed` block, which both readers render.
 *
 * The wrapper is still READ (`isTinymceHtmlDocument`) for documents saved
 * before this change; it is never written again.
 */
export function serializeTinyMceHtmlForApi(html: string): EditorJsOutputData | null {
  const sanitizedHtml = sanitizeHtml(html, { allowSafeIframes: true });
  const blocks = htmlToEditorJsBlocks(sanitizedHtml);

  if (!blocks.length) {
    return null;
  }

  // No `time` and no block `id`s: both are optional to Saleor, neither reader
  // uses them, and a serializer that stamps a clock or random ids makes every
  // save a change (a type-then-undo leaves the form dirty; an untouched
  // description is rewritten on every product save).
  return { version: EDITOR_JS_VERSION, blocks };
}

function htmlToEditorJsBlocks(sanitizedHtml: string): EditorJsBlock[] {
  if (typeof document === "undefined") {
    return htmlSegmentToBlocks(sanitizedHtml);
  }

  const template = document.createElement("template");

  template.innerHTML = sanitizedHtml;
  template.content.querySelectorAll("tfoot").forEach(foldTableFooter);

  const captions = new Map<Element, string>();

  template.content.querySelectorAll("iframe").forEach(iframe => {
    captions.set(iframe, takeFigureCaption(iframe));
    hoistToTopLevel(template.content, iframe);
  });

  const blocks: EditorJsBlock[] = [];
  let segment = "";
  const flush = () => {
    blocks.push(...htmlSegmentToBlocks(segment));
    segment = "";
  };

  template.content.childNodes.forEach(node => {
    if (node instanceof Element && node.tagName === "IFRAME") {
      flush();

      const embed = iframeToEmbedBlock(node, captions.get(node) ?? "");

      if (embed) {
        blocks.push(embed);
      }

      return;
    }

    segment += node instanceof Element ? node.outerHTML : escapeHtml(node.textContent || "");
  });
  flush();

  return blocks;
}

/**
 * Saleor's cleaner keeps `table`, `thead` and `tbody` but not `tfoot`: it
 * unwraps the footer and leaves its rows loose after `</tbody>`. Move them
 * into the body here so what is sent is what Saleor stores.
 */
function foldTableFooter(tfoot: Element): void {
  const table = tfoot.closest("table");

  if (!table) {
    tfoot.remove();

    return;
  }

  let body = Array.from(table.children)
    .filter(child => child.tagName === "TBODY")
    .pop();

  if (!body) {
    body = document.createElement("tbody");
    table.appendChild(body);
  }

  body.append(...Array.from(tfoot.childNodes));
  tfoot.remove();
}

/**
 * `<figure><iframe/><figcaption>…</figcaption></figure>` is one embed with a
 * caption, not an embed beside an orphan caption paragraph: lift the iframe
 * out of the figure and return the caption for the embed block. A figure
 * holding anything else is left for the generic split.
 */
function takeFigureCaption(iframe: Element): string {
  const figure = iframe.parentElement;

  if (!figure || figure.tagName !== "FIGURE") {
    return "";
  }

  const others = Array.from(figure.childNodes).filter(
    child => child !== iframe && !isBlankText(child),
  );
  const caption = others.length === 1 && others[0] instanceof Element && others[0].tagName === "FIGCAPTION"
    ? others[0]
    : null;

  if (others.length && !caption) {
    return "";
  }

  figure.replaceWith(iframe);

  return caption ? caption.innerHTML.trim() : "";
}

/**
 * Split the top-level ancestor of `node` around it so `node` becomes a direct
 * child of `root`: `<p>before<iframe/>after</p>` becomes
 * `<p>before</p><iframe/><p>after</p>`. The cut leaves empty shells on both
 * edges (an item split at its start, a paragraph split at its end), which are
 * trimmed so no empty list item or paragraph is saved; a side left with
 * nothing visible is dropped. An ordered list keeps its numbering across the
 * cut through `start` on the second half.
 */
function hoistToTopLevel(root: DocumentFragment, node: Element): void {
  let top: Node = node;

  while (top.parentNode && top.parentNode !== root) {
    top = top.parentNode;
  }

  if (top === node || !(top instanceof Element)) {
    return;
  }

  const range = document.createRange();

  range.setStartBefore(top);
  range.setEndBefore(node);

  const before = range.extractContents();
  // Captured now: inserting the fragment moves its children into `root`.
  const head = before.firstElementChild;

  root.insertBefore(before, top);
  root.insertBefore(node, top);

  if (head) {
    trimEmptyEdge(head, "last");
  }

  trimEmptyEdge(top, "first");

  if (head?.tagName === "OL" && top.tagName === "OL") {
    continueListNumbering(head, top);
  }

  [head, top].forEach(side => {
    if (side && !hasVisualContent(side.outerHTML, side.textContent || "")) {
      side.remove();
    }
  });
}

/** Remove empty elements along one edge of `element`, outermost first. */
function trimEmptyEdge(element: Element, edge: "first" | "last"): void {
  for (;;) {
    const children = Array.from(element.childNodes).filter(child => !isBlankText(child));
    const child = edge === "first" ? children[0] : children[children.length - 1];

    if (!(child instanceof Element)) {
      return;
    }

    if (!hasVisualContent(child.outerHTML, child.textContent || "")) {
      child.remove();
      continue;
    }

    trimEmptyEdge(child, edge);

    return;
  }
}

/** `second` continues `first`'s item numbers: `start` is what nh3 keeps on `ol`. */
function continueListNumbering(first: Element, second: Element): void {
  const items = Array.from(first.children).filter(child => child.tagName === "LI").length;

  if (!items) {
    return;
  }

  const base = Number(first.getAttribute("start")) || 1;

  second.setAttribute("start", String(base + items));
}

function isBlankText(node: Node): boolean {
  return node.nodeType === 3 && !(node.textContent || "").trim();
}

function htmlSegmentToBlocks(segment: string): EditorJsBlock[] {
  const text = segment.trim();

  if (!text || !hasVisualContent(text, htmlToPlainText(text))) {
    return [];
  }

  return [{ type: "paragraph", data: { text } }];
}

function iframeToEmbedBlock(iframe: Element, caption: string): EditorJsBlock | null {
  const src = iframe.getAttribute("src") || "";

  if (!isSafeEmbedUrl(src)) {
    return null;
  }

  const width = Number(iframe.getAttribute("width")) || undefined;
  const height = Number(iframe.getAttribute("height")) || undefined;
  const service = new URL(src).hostname.includes("vimeo") ? "vimeo" : "youtube";

  return {
    type: "embed",
    data: { service, source: src, embed: src, caption, width, height },
  };
}

export function sanitizeHtml(
  html: string,
  { allowSafeIframes = false }: { allowSafeIframes?: boolean } = {},
): string {
  const preparedHtml = stripUnsafeImages(allowSafeIframes ? stripUnsafeIframes(html) : html);
  const config = allowSafeIframes
    ? {
        ...SANITIZE_CONFIG,
        ...SANITIZE_WITH_IFRAMES_CONFIG,
        ADD_ATTR: [...SANITIZE_CONFIG.ADD_ATTR, ...SANITIZE_WITH_IFRAMES_CONFIG.ADD_ATTR],
      }
    : SANITIZE_CONFIG;

  return DOMPurify.sanitize(preparedHtml, config).trim();
}

function editorJsToHtml(data: EditorJsOutputData): string {
  return data.blocks.map(convertEditorJsBlock).filter(Boolean).join("\n");
}

function convertEditorJsBlock(block: EditorJsBlock): string {
  const { type, data } = block;

  switch (type) {
    case "paragraph":
      return convertEditorJsParagraph(data);

    case "header": {
      const level = clampHeadingLevel(Number(data.level) || 2);

      return `<h${level}>${toString(data.text)}</h${level}>`;
    }

    case "list": {
      const tag = data.style === "ordered" ? "ol" : "ul";
      const items = Array.isArray(data.items) ? data.items : [];
      const html = items.map(convertEditorJsListItem).join("");

      return `<${tag}>${html}</${tag}>`;
    }

    case "quote": {
      const caption = data.caption ? `<cite>${toString(data.caption)}</cite>` : "";

      return `<blockquote><p>${toString(data.text)}</p>${caption}</blockquote>`;
    }

    case "embed":
      return convertEditorJsEmbed(data);

    case "image": {
      const file = isRecord(data.file) ? data.file : undefined;
      const src = toString(file?.url ?? data.url);
      const caption = toString(data.caption);
      const alt = escapeHtml(caption);
      const figcaption = caption ? `<figcaption>${caption}</figcaption>` : "";

      return src ? `<figure><img src="${escapeHtml(src)}" alt="${alt}" />${figcaption}</figure>` : "";
    }

    case "code":
      return `<pre><code>${escapeHtml(toString(data.code))}</code></pre>`;

    case "delimiter":
      return "<hr />";

    case "table":
      return convertEditorJsTable(data);

    case "checklist":
      return convertEditorJsChecklist(data);

    case "warning":
      return `<aside><strong>${toString(data.title)}</strong><p>${toString(data.message)}</p></aside>`;

    default:
      return `<!-- unhandled-editorjs-block type="${escapeHtml(type)}" -->`;
  }
}

function convertEditorJsParagraph(data: Record<string, unknown>): string {
  const text = toString(data.text);

  if (hasBlockLevelHtml(text)) {
    return text;
  }

  return `<p>${text}</p>`;
}

function hasBlockLevelHtml(value: string): boolean {
  return BLOCK_LEVEL_HTML_TAG_PATTERN.test(value);
}

function convertEditorJsListItem(item: unknown): string {
  if (typeof item === "string") {
    return `<li>${item}</li>`;
  }

  if (!isRecord(item)) {
    return "";
  }

  const content = toString(item.content ?? item.text);
  const nestedItems = Array.isArray(item.items) ? item.items.map(convertEditorJsListItem).join("") : "";
  const nestedList = nestedItems ? `<ul>${nestedItems}</ul>` : "";

  return `<li>${content}${nestedList}</li>`;
}

function convertEditorJsEmbed(data: Record<string, unknown>): string {
  const src = toString(data.embed);
  const caption = toString(data.caption);

  if (isSafeEmbedUrl(src)) {
    const width = Number(data.width) || 600;
    const height = Number(data.height) || 400;
    const figcaption = caption ? `<figcaption>${caption}</figcaption>` : "";

    return `<figure><iframe src="${escapeHtml(src)}" width="${width}" height="${height}" loading="lazy" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>${figcaption}</figure>`;
  }

  if (isHttpUrl(src)) {
    return `<p><a href="${escapeHtml(src)}" rel="noopener noreferrer" target="_blank">${
      caption ? toString(caption) : escapeHtml(src)
    }</a></p>`;
  }

  return caption ? `<p>${caption}</p>` : "";
}

function convertEditorJsTable(data: Record<string, unknown>): string {
  const rows = Array.isArray(data.content) ? data.content : [];

  if (!rows.length) {
    return "";
  }

  const html = rows
    .map((row, rowIndex) => {
      if (!Array.isArray(row)) {
        return "";
      }

      const cells = row
        .map(cell => {
          const tag = rowIndex === 0 && data.withHeadings ? "th" : "td";

          return `<${tag}>${toString(cell)}</${tag}>`;
        })
        .join("");

      return `<tr>${cells}</tr>`;
    })
    .join("");

  return `<table><tbody>${html}</tbody></table>`;
}

function convertEditorJsChecklist(data: Record<string, unknown>): string {
  const items = Array.isArray(data.items) ? data.items : [];
  const html = items
    .map(item => {
      if (!isRecord(item)) {
        return "";
      }

      const checked = item.checked ? " data-checked=\"true\"" : "";

      return `<li${checked}>${toString(item.text)}</li>`;
    })
    .join("");

  return `<ul data-list-type="checklist">${html}</ul>`;
}

function stripUnsafeIframes(html: string): string {
  if (typeof document === "undefined") {
    return html;
  }

  const template = document.createElement("template");

  template.innerHTML = html;
  template.content.querySelectorAll("iframe").forEach(iframe => {
    const src = iframe.getAttribute("src") || "";

    if (!isSafeEmbedUrl(src)) {
      iframe.remove();

      return;
    }

    iframe.setAttribute("loading", iframe.getAttribute("loading") || "lazy");
    iframe.setAttribute(
      "referrerpolicy",
      iframe.getAttribute("referrerpolicy") || "strict-origin-when-cross-origin",
    );
  });

  return template.innerHTML;
}

function stripUnsafeImages(html: string): string {
  if (typeof document === "undefined") {
    return html;
  }

  const template = document.createElement("template");

  template.innerHTML = html;
  template.content.querySelectorAll("img").forEach(image => {
    const src = image.getAttribute("src") || "";

    if (!isSafeImageUrl(src)) {
      image.remove();

      return;
    }

    image.setAttribute("loading", image.getAttribute("loading") || "lazy");
  });

  return template.innerHTML;
}

function htmlToPlainText(html: string): string {
  if (!html) {
    return "";
  }

  if (typeof document === "undefined") {
    return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  }

  const template = document.createElement("template");

  template.innerHTML = sanitizeHtml(html);
  template.content
    .querySelectorAll("address,article,aside,blockquote,br,caption,cite,dd,div,dt,figcaption,footer,h1,h2,h3,h4,h5,h6,header,hr,li,p,pre,section,td,th")
    .forEach(node => node.after(document.createTextNode(" ")));

  return (template.content.textContent || "").replace(/\s+/g, " ").trim();
}

function hasVisualContent(html: string, plainText: string): boolean {
  if (plainText.trim()) {
    return true;
  }

  return /<(img|iframe|table|hr)\b/i.test(html);
}

function isSafeImageUrl(value: string): boolean {
  const trimmed = value.trim();

  if (trimmed.startsWith("/") && !trimmed.startsWith("//")) {
    return true;
  }

  return isHttpUrl(trimmed);
}

function isSafeEmbedUrl(value: string): boolean {
  try {
    const url = new URL(value);

    return url.protocol === "https:" && ALLOWED_EMBED_HOSTS.has(url.hostname);
  } catch {
    return false;
  }
}

function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);

    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function clampHeadingLevel(level: number): number {
  return Math.max(1, Math.min(6, level));
}

function toString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
